import * as PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {
  REQUEST_FULFILLMENT_DEFINITION_SHAPE,
  RequestFulfillmentDefinition,
} from 'requests/fulfillments/FulfillmentConstants';
import Col from 'ui-library/bs/Col';
import FormInput from 'ui-library/bs/FormInput';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as modals from 'util/Modals';
import {InputChangeEvent} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {CreateWorkflowState} from 'workflows/template-selection/WorkflowTemplateSelectionConstants';
import {getWorkflowDisplayString} from 'workflows/util/WorkflowUtil';

import * as WorkflowTemplateSelectionReduxActions from '../template-selection/WorkflowTemplateSelectionReduxActions';
import WorkflowTemplateSelectionTable from '../template-selection/WorkflowTemplateSelectionTable';
import * as WorkflowActions from '../WorkflowActions';
import * as WorkflowTemplateActions from '../WorkflowTemplateActions';

interface PropsFromConnect {
  workflowTemplateSelectionState: CreateWorkflowState;
}

function mapStateToProps(state: BenchlingState): PropsFromConnect {
  return {
    workflowTemplateSelectionState: new WorkflowSelector(
      state.models.workflows
    ).getWorkflowTemplateSelectionState(state.ui.workflowTemplateSelection),
  };
}

interface PropsFromDispatch {
  onResetState: () => void;
  onSelectWorkflowTemplateVersion: (workflowTemplateId: string, workflowTemplateVersionId: string) => void;
  onUpdateRootFolder: (rootFolderId: string) => void;
}

function mapDispatchToProps(dispatch: BenchlingDispatch): PropsFromDispatch {
  return {
    onResetState(): void {
      dispatch(WorkflowTemplateSelectionReduxActions.resetState());
    },
    onSelectWorkflowTemplateVersion(workflowTemplateId: string, workflowTemplateVersionId: string): void {
      dispatch(
        WorkflowTemplateSelectionReduxActions.selectWorkflowTemplateVersion(
          workflowTemplateId,
          workflowTemplateVersionId
        )
      );
    },
    onUpdateRootFolder(rootFolderId: string): void {
      dispatch(WorkflowTemplateSelectionReduxActions.updateRootFolder(rootFolderId));
    },
  };
}

interface CreateWorkflowModalProps {
  onRequestClose: () => void;
  initialWorkflowTemplateId?: string;
  initialWorkflowTemplateVersionId?: string;
  fulfillments?: Array<RequestFulfillmentDefinition>;
}

type ConnectedCreateWorkflowModalProps = CreateWorkflowModalProps & PropsFromDispatch & PropsFromConnect;

interface State {
  name: string;
}

class CreateWorkflowModal extends React.Component<ConnectedCreateWorkflowModalProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    onRequestClose: PropTypes.func.isRequired,
    initialWorkflowTemplateId: PropTypes.string,
    initialWorkflowTemplateVersionId: PropTypes.string,
    fulfillments: PropTypes.arrayOf(REQUEST_FULFILLMENT_DEFINITION_SHAPE.isRequired),
    // Props provided by connect
    // Should have type CreateWorkflowState
    workflowTemplateSelectionState: PropTypes.object.isRequired,
    onResetState: PropTypes.func.isRequired,
    onSelectWorkflowTemplateVersion: PropTypes.func.isRequired,
    onUpdateRootFolder: PropTypes.func.isRequired,
  };

  override state: State = {
    name: '',
  };

  override componentDidMount(): void {
    this.props.onResetState();
    if (this.props.initialWorkflowTemplateId && this.props.initialWorkflowTemplateVersionId) {
      this.props.onSelectWorkflowTemplateVersion(
        this.props.initialWorkflowTemplateId,
        this.props.initialWorkflowTemplateVersionId
      );
    }
    WorkflowTemplateActions.fetchAllWorkflowTemplates();
  }

  _handleRequestSubmit = (): void => {
    WorkflowActions.createWorkflow(
      {
        name: this.state.name,
        fulfillments: this.props.fulfillments,
        rootFolderId: this.props.workflowTemplateSelectionState.rootFolderId!,
        workflowTemplateVersionId:
          this.props.workflowTemplateSelectionState.selectedWorkflowTemplateVersionId,
      },
      {
        onSuccess: this.props.onRequestClose,
      }
    );
  };

  _handleStartFromScratch = (): void => {
    modals
      .prompt('Workflow ID prefix', '', (prefix: string) => prefix.length > 0)
      .done((prefix: string) => {
        WorkflowActions.createWorkflow(
          {
            prefix,
            name: this.state.name,
            rootFolderId: this.props.workflowTemplateSelectionState.rootFolderId!,
            fulfillments: this.props.fulfillments,
          },
          {
            onSuccess: this.props.onRequestClose,
          }
        );
      });
  };

  _handleFolderChange = ({target: {value}}: InputChangeEvent): void => {
    FolderDefaults.set(FolderDefaultsKey.WORKFLOWS, value);
    this.props.onUpdateRootFolder(value);
  };

  _handleNameChange = ({target: {value}}: InputChangeEvent): void => {
    this.setState({
      name: value,
    });
  };

  _getButtons(): Array<ModalButton> {
    return [
      {
        text: 'Start from scratch',
        style: ButtonStyle.TERTIARY_BLUE,
        disabled: !this.props.workflowTemplateSelectionState.rootFolderId || this.state.name.length === 0,
        onClick: this._handleStartFromScratch,
        pullLeft: true,
        key: 'scratch',
      },
      {
        text: 'Create',
        submit: true,
        disabled:
          !this.props.workflowTemplateSelectionState.rootFolderId ||
          this.props.workflowTemplateSelectionState.selectedWorkflowTemplateVersionId == null ||
          this.state.name.length === 0,
        key: 'select',
      },
    ];
  }

  override render(): JSX.Element {
    return (
      <Modal
        title={getWorkflowDisplayString('Create legacy workflow', 'Create workflow')}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handleRequestSubmit}
        buttons={this._getButtons()}
        wrapInForm={false}
        analyticsModule={ANALYTICS_MODULE.LEGACY_WORKFLOWS}
        analyticsModalType="create_workflow"
      >
        <Row>
          <Col xs={6}>
            <FieldLabel isRequired={true} label="Name" />
            <FormInput focusOnMount={true} value={this.state.name} onChange={this._handleNameChange} />
          </Col>
          <Col xs={6}>
            <FieldLabel isRequired={true} label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} />
            <FolderSelector
              level="append"
              noFolderText="Choose folder"
              value={this.props.workflowTemplateSelectionState.rootFolderId}
              onChange={this._handleFolderChange}
              /* Only render notebook folders */
              showInventory={false}
              showNotebook={true}
              showRegistries={false}
              unsetOnFetchFail={true}
            />
          </Col>
        </Row>
        <Row className="u-small-spacing-top">
          <Col xs={12}>
            {getWorkflowDisplayString(
              'Permissions for this legacy workflow will be derived from the above specified project.',
              'Permissions for this workflow will be derived from the above specified project.'
            )}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col xs={12}>
            {this.props.workflowTemplateSelectionState.isLoading ? (
              <div className="text-center">
                <HorizontalThrobber />
              </div>
            ) : (
              <WorkflowTemplateSelectionTable
                tableRows={this.props.workflowTemplateSelectionState.tableRows!}
              />
            )}
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateWorkflowModal) as React.ComponentType<CreateWorkflowModalProps>;
